import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Mic, MicOff, Play, Pause, Square, RotateCcw } from 'lucide-react'
import { Button } from '../ui/Button'
import toast from 'react-hot-toast'

interface VoiceRecorderProps {
  onRecordingComplete: (audioBlob: Blob, duration: number) => void
  maxDuration?: number
  autoStart?: boolean
  className?: string
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  maxDuration = 300, // 5 minutes default
  autoStart = false,
  className = ''
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null)
  const [audioLevel, setAudioLevel] = useState(0)
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  useEffect(() => {
    // Initialize AudioContext
    const initAudio = () => {
      try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext
        if (AudioContext && !audioContext) {
          setAudioContext(new AudioContext())
        }
      } catch (error) {
        console.error('Failed to initialize AudioContext:', error)
      }
    }

    // Initialize on first user interaction
    const handleInteraction = () => {
      initAudio()
      document.removeEventListener('click', handleInteraction)
    }

    document.addEventListener('click', handleInteraction)
    
    if (autoStart) {
      startRecording()
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      document.removeEventListener('click', handleInteraction)
    }
  }, [autoStart, audioContext])

  const updateAudioLevel = () => {
    if (!analyserRef.current) return
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
    analyserRef.current.getByteFrequencyData(dataArray)
    
    // Calculate average level
    const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length
    const normalizedLevel = average / 255 // Normalize to 0-1
    
    setAudioLevel(normalizedLevel)
    
    animationFrameRef.current = requestAnimationFrame(updateAudioLevel)
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      })
      
      // Set up audio analysis if context exists
      if (audioContext) {
        const source = audioContext.createMediaStreamSource(stream)
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        source.connect(analyser)
        analyserRef.current = analyser
        
        // Start visualizing audio levels
        updateAudioLevel()
      }
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      })
      
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        setAudioBlob(blob)
        
        const url = URL.createObjectURL(blob)
        setAudioUrl(url)
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop())
        
        // Stop audio visualization
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }
      }
      
      mediaRecorder.start(100) // Collect data every 100ms
      setIsRecording(true)
      setDuration(0)
      
      // Start timer
      timerRef.current = setInterval(() => {
        setDuration(prev => {
          const newDuration = prev + 1
          if (newDuration >= maxDuration) {
            stopRecording()
            return maxDuration
          }
          return newDuration
        })
      }, 1000)
      
      toast.success('Recording started')
    } catch (error) {
      console.error('Error starting recording:', error)
      toast.error('Failed to start recording. Please check microphone permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop()
        }
        setIsRecording(false)
        
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
        
        // Stop audio visualization
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
        }
        
        toast.success('Recording stopped')
      } catch (error) {
        console.error('Error stopping recording:', error)
        toast.error('Failed to stop recording')
      }
    }
  }

  const playRecording = () => {
    if (audioUrl && audioRef.current) {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const pauseRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    }
  }

  const resetRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    
    setIsPlaying(false)
    setAudioBlob(null)
    setDuration(0)
    
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
      setAudioUrl(null)
    }
  }

  const handleComplete = () => {
    if (audioBlob) {
      onRecordingComplete(audioBlob, duration)
      resetRecording()
    }
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progressPercentage = (duration / maxDuration) * 100

  return (
    <div className={`voice-recorder ${className}`}>
      {/* Recording Visualization */}
      <div className="relative mb-6">
        <div className="w-32 h-32 mx-auto relative">
          {/* Outer ring */}
          <div className="absolute inset-0 rounded-full border-4 border-sage-200">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="50%"
                cy="50%"
                r="46%"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeDasharray={`${progressPercentage * 2.83} 283`}
                className="text-sage-500 transition-all duration-1000"
              />
            </svg>
          </div>
          
          {/* Audio level visualization */}
          {isRecording && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-full flex items-center justify-center">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1 mx-0.5 bg-sage-400 rounded-full"
                    animate={{ 
                      height: isRecording ? `${10 + audioLevel * 40 + Math.random() * 20}px` : '10px'
                    }}
                    transition={{ duration: 0.1 }}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Center button */}
          <motion.div
            className="absolute inset-4 rounded-full bg-gradient-to-r from-sage-500 to-emerald-500 flex items-center justify-center cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={isRecording ? { scale: [1, 1.1, 1] } : {}}
            transition={isRecording ? { duration: 1, repeat: Infinity } : {}}
            onClick={isRecording ? stopRecording : startRecording}
          >
            {isRecording ? (
              <Square className="w-8 h-8 text-white" />
            ) : (
              <Mic className="w-8 h-8 text-white" />
            )}
          </motion.div>
          
          {/* Recording indicator */}
          {isRecording && (
            <motion.div
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <div className="w-2 h-2 bg-white rounded-full" />
            </motion.div>
          )}
        </div>
      </div>

      {/* Timer */}
      <div className="text-center mb-4">
        <div className="text-2xl font-mono font-bold text-slate-900">
          {formatTime(duration)}
        </div>
        <div className="text-sm text-slate-500">
          Max: {formatTime(maxDuration)}
        </div>
      </div>

      {/* Audio playback */}
      {audioUrl && (
        <div className="mb-6">
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            className="hidden"
          />
          
          <div className="flex items-center justify-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={isPlaying ? pauseRecording : playRecording}
              className="border-sage-300 text-sage-700 hover:bg-sage-50"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={resetRecording}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex justify-center space-x-4">
        {!isRecording && !audioBlob && (
          <Button
            onClick={startRecording}
            className="bg-sage-600 hover:bg-sage-700 text-white px-8"
          >
            <Mic className="w-4 h-4 mr-2" />
            Start Recording
          </Button>
        )}
        
        {isRecording && (
          <Button
            onClick={stopRecording}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50 px-8"
          >
            <Square className="w-4 h-4 mr-2" />
            Stop Recording
          </Button>
        )}
        
        {audioBlob && !isRecording && (
          <div className="flex space-x-3">
            <Button
              onClick={resetRecording}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Re-record
            </Button>
            
            <Button
              onClick={handleComplete}
              className="bg-sage-600 hover:bg-sage-700 text-white"
            >
              Complete Assessment
            </Button>
          </div>
        )}
      </div>

      {/* Recording tips */}
      <div className="mt-6 text-center text-sm text-slate-500">
        <p>💡 Speak clearly and naturally. Find a quiet space for best results.</p>
        {isRecording && (
          <p className="mt-1 text-sage-600">🎙️ Recording in progress...</p>
        )}
      </div>
    </div>
  )
}